<script lang="ts" setup>
import type { ProductListItem } from "~~/models/products";
import type { ProductSearchDto } from "~~/server/api/ecom/products/search.post";
import type { ProductSelectionSectionRecord } from "~~/server/graphql/datocms/generated";
import type { PlanktonProductSearchIdResponse, PlanktonProductSearchResponse } from "~~/server/plankton";

const { data } = defineProps<{ data: ProductSelectionSectionRecord }>();

const { locale } = useI18n();
const { loggedIn } = useUserSession();

const layout = await useLayout();
const cmsData = await useCatalogPage();

const productIdFilterList = (data.products as Array<{ name: string; value: string }> | undefined)?.map(product => Number(product.value)) ?? [];
const { data: productSelections } = await useAsyncData(`product-selections-${productIdFilterList.join(",")}`, async () => {
  if (!productIdFilterList.length) {
    return [];
  }

  const response = await $fetch<{ isFallback: boolean; results?: PlanktonProductSearchResponse | undefined }>("/api/ecom/products/search", {
    method: "POST",
    body: {
      productIdFilterList: productIdFilterList,
      pageSize: 100,
    } as ProductSearchDto,
  });

  const productIdList = response?.results?.pagedProducts[1]
    ?.sort((a: PlanktonProductSearchIdResponse, b: PlanktonProductSearchIdResponse) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    ?.map((p: PlanktonProductSearchIdResponse) => p.productId) ?? [];

  const orderedProductIdList = productIdFilterList.filter(id => productIdList.includes(id));
  return await $fetch<Array<ProductListItem>>("/api/ecom/products/list", {
    query: {
      productIdList: orderedProductIdList,
      addCategories: true,
      addExternalData: true,
      addFiles: true,
      addOverpacks: true,
      addOverpackTranslations: true,
      addProductTranslations: true,
      addPropertyValues: true,
      locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
    },
  });
});

// only load prices when authenticated
const { prices, isPricesLoading } = usePricing(productSelections, { loggedIn: loggedIn.value });

// swiper
const swiperBreakpoints = {
  0: { slidesPerView: 2, slidesPerGroup: Math.min(productIdFilterList.length, 2), spaceBetween: 16 },
  768: { slidesPerView: 3, slidesPerGroup: Math.min(productIdFilterList.length, 3), spaceBetween: 16 },
  1400: { slidesPerView: 4, slidesPerGroup: Math.min(productIdFilterList.length, 4), spaceBetween: 16 },
};

useSwiper();
</script>

<template>
  <div v-if="productSelections?.length && cmsData && layout" class="mb-3xl lg:mb-5xl">
    <CenterCard view-mode="wide" class="gap-xl flex flex-col">
      <h3 class="text-center">
        {{ data.title }}
      </h3>

      <div>
        <swiper-container :navigation="true" :loop="true" :breakpoints="swiperBreakpoints">
          <swiper-slide v-for="(product, index) of productSelections" :key="product?.id">
            <ProductCardItem
              :layout="layout!"
              :cms-data="cmsData!"
              :product="product"
              :prices="prices"
              :is-prices-loading="isPricesLoading"
              :overpack-popper="{ placement: 'top' }"
              track-creative-events-context="Product Selection"
              :index="index"
            />
          </swiper-slide>
        </swiper-container>
      </div>
    </CenterCard>
  </div>
</template>

<style scoped>

</style>
